/**
 * The words the family reads, in one file.
 *
 * Two rules are enforced here rather than remembered:
 *
 * * **A child never sees a number about themselves** (invariant 1). A mastery
 *   state has a label; it has no score, and there is no function here that
 *   would produce one.
 * * **Fluency is never called "speed"** in a child-facing string. Baya is slow
 *   and knows it; naming it would make the interface one more place that says
 *   so. It is called "automatismes" or "réflexes" — which is also what it
 *   actually is.
 */

/**
 * **Le libellé d'une matière vit dans `ui/matieres`**, avec son emblème.
 *
 * Cette table le portait de son côté depuis toujours, et `child/journee` en
 * portait une troisième. Trois tables pour la même chose finissent par diverger,
 * et une matière ajoutée à l'une manque aux autres sans que rien ne le dise —
 * c'est la « configuration parallèle à maintenir » que `web-pwa` §4.2 refuse.
 *
 * `subjectLabel` reste ici, et rend exactement ce qu'elle rendait : la forme de
 * prose, minuscule et développée. Seule sa source a changé.
 */
import type { Tone } from "@/config/profile";

export { enProse as subjectLabel } from "@/ui/matieres";

/** The states of `mastery` (§13.4), spelled as they are stored. */
export type MasteryState = "non_evalue" | "fragile" | "en_cours" | "acquis" | "consolide";

export const MASTERY_ORDER: readonly MasteryState[] = [
  "non_evalue",
  "fragile",
  "en_cours",
  "acquis",
  "consolide",
];

/** What a **child** reads. Forward-looking, never a verdict. */
export const CHILD_STATE_LABEL: Record<MasteryState, string> = {
  non_evalue: "pas encore vu",
  fragile: "à revoir",
  en_cours: "en cours",
  acquis: "acquis",
  consolide: "consolidé",
};

/** What a **parent** reads. The vocabulary of `mastery`, unsoftened. */
export const PARENT_STATE_LABEL: Record<MasteryState, string> = {
  non_evalue: "non évalué",
  fragile: "fragile",
  en_cours: "en cours",
  acquis: "acquis",
  consolide: "consolidé",
};

export const FLUENCY_CHILD_LABEL = "automatismes";
export const FLUENCY_CHILD_HINT = "tes réflexes sur cette compétence";

export function isMasteryState(value: unknown): value is MasteryState {
  return typeof value === "string" && (MASTERY_ORDER as readonly string[]).includes(value);
}

/** Milestones worth celebrating in the session (SPEC §12). No streak, ever. */
export function isMilestone(previous: MasteryState, next: MasteryState): boolean {
  const gained = MASTERY_ORDER.indexOf(next) > MASTERY_ORDER.indexOf(previous);
  return gained && (next === "acquis" || next === "consolide");
}

export const MILESTONE_SENTENCE: Record<"acquis" | "consolide", string> = {
  acquis: "Cette compétence est acquise.",
  consolide: "Cette compétence est consolidée. Elle tient dans le temps.",
};

/**
 * « Ma semaine » (SPEC §12) : les phrases, selon le ton du profil.
 *
 * **Aucun chiffre, aucune comparaison** — ni « 3 compétences », ni « plus que
 * la semaine dernière » (invariant §14.1). Une semaine vide n'est pas un
 * reproche : pour un collégien elle se dit en encourageant, pour un lycéen elle
 * se constate, et ni l'une ni l'autre ne parle de l'enfant.
 */
export const WEEK_LEAD: Record<Tone, string> = {
  encouraging: "Ce qui a passé un cap ces derniers jours.",
  factual: "Passé en acquis ou consolidé ces sept derniers jours.",
};

export const WEEK_EMPTY: Record<Tone, string> = {
  encouraging: "Rien de nouveau ici pour l'instant. Chaque exercice prépare la suite.",
  factual: "Aucune compétence n'est passée en acquis ou consolidé ces sept derniers jours.",
};

/** Le mot d'état accordé à « compétence », au féminin. */
export const WEEK_STATE_WORD: Record<"acquis" | "consolide", string> = {
  acquis: "acquise",
  consolide: "consolidée",
};
